import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkle, Check, Palette, Plus } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";
import { generateCarousel, listBrands, fileUrl } from "../lib/api";
import { FORMATS } from "../lib/formats";
import { DESIGN_STYLES } from "../lib/designStyles";
import { toast } from "sonner";

const STEPS_COUNT = 6;

export default function CreateWizard() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    product_description: "",
    price_tier: "mid",
    dm_keyword: "",
    slides_count: 10,
    conversion_boost: true,
    audience: "",
    funnel_stage: "new",
    framework: "pas",
    tone: "guide",
    design_style: "none",
    language: lang,
    format: "1x1",
    brand_id: "",
  });
  const [brands, setBrands] = useState([]);
  useEffect(() => {
    listBrands().then(setBrands).catch(() => setBrands([]));
  }, []);

  const set = (patch) => setForm((p) => ({ ...p, ...patch }));

  const canContinue = useMemo(() => {
    if (step === 1) return form.product_description.trim().length > 10;
    if (step === 3) return form.audience.trim().length > 5;
    return true;
  }, [step, form]);

  const submit = async () => {
    setLoading(true);
    try {
      const data = await generateCarousel(form);
      toast.success(lang === "el" ? "Έτοιμο!" : "Ready!");
      nav(`/editor/${data.id}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || (lang === "el" ? "Σφάλμα δημιουργίας" : "Generation failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-6 md:px-10 pt-12 pb-24 max-w-4xl mx-auto" data-testid="wizard-page">
      {/* Steps indicator */}
      <div className="flex items-center gap-2 mb-10" data-testid="step-indicator">
        {Array.from({ length: STEPS_COUNT }).map((_, i) => {
          const n = i + 1;
          const state = n < step ? "is-done" : n === step ? "is-current" : "";
          return (
            <React.Fragment key={n}>
              <button onClick={() => n < step && setStep(n)} className={`step-dot ${state}`} data-testid={`step-dot-${n}`}>
                {n < step ? <Check size={12} weight="bold" /> : n}
              </button>
              {n < STEPS_COUNT && <div className={`flex-1 h-px ${n < step ? "bg-forest" : "bg-line"}`} />}
            </React.Fragment>
          );
        })}
      </div>

      <div className="text-sm text-ink-soft mb-3">
        {t("wizard_step_of")} {step} {t("wizard_of")} {STEPS_COUNT}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
        >
          {step === 1 && <Step1 form={form} set={set} />}
          {step === 2 && <Step2 form={form} set={set} />}
          {step === 3 && <Step3 form={form} set={set} />}
          {step === 4 && <Step4 form={form} set={set} />}
          {step === 5 && <Step5 form={form} set={set} />}
          {step === 6 && <Step6 form={form} set={set} brands={brands} />}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="mt-10 flex items-center justify-between" data-testid="wizard-nav">
        <button
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          disabled={step === 1 || loading}
          className="inline-flex items-center gap-2 text-ink-soft hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed text-sm"
          data-testid="wizard-back"
        >
          <ArrowLeft size={14} weight="bold" /> {t("wizard_back")}
        </button>

        {step < STEPS_COUNT ? (
          <button
            onClick={() => canContinue && setStep((s) => s + 1)}
            disabled={!canContinue}
            className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-6 py-3 text-sm font-medium hover:bg-forest-dark transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            data-testid="wizard-next"
          >
            {t("wizard_next")} <ArrowRight size={14} weight="bold" />
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-7 py-3.5 text-sm font-medium hover:bg-forest-dark transition-all hover:-translate-y-0.5 disabled:opacity-60"
            data-testid="wizard-generate"
          >
            <Sparkle weight="duotone" size={16} />
            {loading ? t("generating") : t("wizard_generate")}
          </button>
        )}
      </div>
    </div>
  );
}

function Heading({ kicker, title, sub }) {
  return (
    <div className="mb-8">
      <div className="kicker-label mb-3">— {kicker}</div>
      <h2 className="font-heading font-semibold text-[32px] md:text-[42px] leading-tight tracking-tight text-ink">{title}</h2>
      <p className="mt-3 text-ink-soft text-base">{sub}</p>
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} className={`chip ${active ? "is-active" : ""} px-4 py-2 rounded-full text-sm`} data-testid={testid}>
      {children}
    </button>
  );
}

function CardOption({ active, onClick, title, desc, testid }) {
  return (
    <button
      onClick={onClick}
      className={`text-left p-5 rounded-2xl border transition ${
        active ? "border-forest bg-forest/5" : "border-line bg-white hover:border-forest/40"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-heading font-medium text-ink">{title}</div>
        {active && <Check size={16} weight="bold" className="text-forest" />}
      </div>
      <div className="text-sm text-ink-soft">{desc}</div>
    </button>
  );
}

// ---- Steps ----
function Step1({ form, set }) {
  const { t } = useI18n();
  return (
    <div>
      <Heading kicker="01 · brief" title={t("step1_title")} sub={t("step1_sub")} />
      <textarea
        value={form.product_description}
        onChange={(e) => set({ product_description: e.target.value })}
        rows={5}
        placeholder={t("step1_placeholder")}
        className="cs-input w-full"
        data-testid="input-product-description"
      />
      <div className="mt-6">
        <div className="kicker-label mb-3">{t("step1_price")}</div>
        <div className="flex gap-2" data-testid="price-group">
          <Chip active={form.price_tier === "low"} onClick={() => set({ price_tier: "low" })} testid="price-low">{t("step1_price_low")}</Chip>
          <Chip active={form.price_tier === "mid"} onClick={() => set({ price_tier: "mid" })} testid="price-mid">{t("step1_price_mid")}</Chip>
          <Chip active={form.price_tier === "high"} onClick={() => set({ price_tier: "high" })} testid="price-high">{t("step1_price_high")}</Chip>
        </div>
      </div>
    </div>
  );
}

function Step2({ form, set }) {
  const { t } = useI18n();
  return (
    <div>
      <Heading kicker="02 · trigger" title={t("step2_title")} sub={t("step2_sub")} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="kicker-label mb-2">{t("step2_keyword")}</div>
          <input
            value={form.dm_keyword}
            onChange={(e) => set({ dm_keyword: e.target.value })}
            placeholder={t("step2_keyword_ph")}
            className="cs-input w-full"
            data-testid="input-dm-keyword"
          />
        </div>
        <div>
          <div className="kicker-label mb-2">{t("step2_slides")}: <span className="text-forest font-semibold">{form.slides_count}</span></div>
          <input
            type="range"
            min={3}
            max={20}
            step={1}
            value={form.slides_count}
            onChange={(e) => set({ slides_count: parseInt(e.target.value) })}
            className="w-full accent-[#244A36]"
            data-testid="input-slides-count"
          />
          <div className="flex justify-between text-xs text-ink-soft mt-1"><span>3</span><span>20</span></div>
        </div>
      </div>

      <label className="mt-8 flex items-center justify-between gap-4 p-5 rounded-2xl border border-line cursor-pointer hover:border-forest/50 transition" data-testid="input-conversion-boost">
        <div>
          <div className="font-heading font-medium text-ink">{t("step2_boost")}</div>
          <div className="text-sm text-ink-soft">{t("step2_boost_desc")}</div>
        </div>
        <input
          type="checkbox"
          checked={form.conversion_boost}
          onChange={(e) => set({ conversion_boost: e.target.checked })}
          className="w-5 h-5 accent-[#244A36] cursor-pointer"
        />
      </label>
    </div>
  );
}

function Step3({ form, set }) {
  const { t } = useI18n();
  return (
    <div>
      <Heading kicker="03 · audience" title={t("step3_title")} sub={t("step3_sub")} />
      <textarea
        rows={4}
        value={form.audience}
        onChange={(e) => set({ audience: e.target.value })}
        placeholder={t("step3_placeholder")}
        className="cs-input w-full"
        data-testid="input-audience"
      />
      <div className="mt-6">
        <div className="kicker-label mb-3">{t("step3_funnel")}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="funnel-group">
          <CardOption active={form.funnel_stage === "new"} onClick={() => set({ funnel_stage: "new" })} title={t("step3_new")} desc="awareness" testid="funnel-new" />
          <CardOption active={form.funnel_stage === "warm"} onClick={() => set({ funnel_stage: "warm" })} title={t("step3_warm")} desc="trust · proof" testid="funnel-warm" />
          <CardOption active={form.funnel_stage === "hot"} onClick={() => set({ funnel_stage: "hot" })} title={t("step3_hot")} desc="offer · urgency" testid="funnel-hot" />
        </div>
      </div>
    </div>
  );
}

function Step4({ form, set }) {
  const { t } = useI18n();
  const list = [
    { k: "pattern_interrupt", funnel: "top" },
    { k: "myth_busting", funnel: "top" },
    { k: "authority", funnel: "mid" },
    { k: "soft_story", funnel: "mid" },
    { k: "comparison", funnel: "mid" },
    { k: "objection", funnel: "bottom" },
    { k: "direct_sale", funnel: "bottom" },
    { k: "belief_shift", funnel: "top" },
    { k: "pas", funnel: "mid" },
    { k: "social_proof", funnel: "bottom" },
    { k: "fomo", funnel: "bottom" },
    { k: "value_ladder", funnel: "mid" },
    { k: "dream_outcome", funnel: "top" },
    { k: "curiosity_gap", funnel: "top" },
    { k: "transformation", funnel: "mid" },
  ];
  const funnelLabel = (f) => t(`funnel_${f}`);
  return (
    <div>
      <Heading kicker="04 · framework" title={t("step4_title")} sub={t("step4_sub")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="framework-group">
        {list.map((f) => (
          <button
            key={f.k}
            onClick={() => set({ framework: f.k })}
            className={`text-left p-4 rounded-2xl border transition ${
              form.framework === f.k ? "border-forest bg-forest/5" : "border-line bg-white hover:border-forest/40"
            }`}
            data-testid={`framework-${f.k}`}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="font-heading font-medium text-ink">{t(`fw_${f.k}`)}</div>
              {form.framework === f.k && <Check size={14} weight="bold" className="text-forest" />}
            </div>
            <div className="text-sm text-ink-soft mb-3">{t(`fw_${f.k}_d`)}</div>
            <span
              className="inline-block text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border"
              style={{
                borderColor: f.funnel === "top" ? "#244A36" : f.funnel === "mid" ? "#C48A4E" : "#8B3A2E",
                color: f.funnel === "top" ? "#244A36" : f.funnel === "mid" ? "#C48A4E" : "#8B3A2E",
              }}
            >
              {funnelLabel(f.funnel)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Step5({ form, set }) {
  const { t, lang } = useI18n();
  const tones = ["trailblazer", "guide", "maverick", "elite_ceo", "analytical", "coach"];
  const styles = [
    "none", "shruti", "hormozi", "gary_vee", "alex_catoni", "marie_forleo",
    "devin_jatho", "dain_walker", "chris_do", "marketing_harry", "custom"
  ];
  return (
    <div>
      <Heading kicker="05 · voice" title={t("step5_title")} sub={t("step5_sub")} />
      <div className="space-y-8">
        <div>
          <div className="kicker-label mb-3">{t("tone_label")}</div>
          <div className="flex flex-wrap gap-2" data-testid="tone-group">
            {tones.map((k) => (
              <Chip key={k} active={form.tone === k} onClick={() => set({ tone: k })} testid={`tone-${k}`}>{t(`tone_${k}`)}</Chip>
            ))}
          </div>
        </div>
        <div>
          <div className="kicker-label mb-3">{t("design_label")}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="design-group">
            {styles.map((k) => (
              <DesignStyleCard
                key={k}
                styleKey={k}
                label={t(`design_${k}`)}
                active={form.design_style === k}
                lang={lang}
                onClick={() => set({ design_style: k })}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DesignStyleCard({ styleKey, label, active, lang, onClick }) {
  const meta = DESIGN_STYLES[styleKey] || DESIGN_STYLES.none;
  const p = meta.preview;
  const desc = meta[lang] || meta.en;
  const fontClass =
    p.font === "serif" ? "font-heading"
    : p.font === "bold" ? "font-display font-black"
    : "font-display";
  return (
    <div className="relative group">
      <button
        onClick={onClick}
        className={`w-full text-left rounded-2xl border transition overflow-hidden ${
          active ? "border-forest ring-2 ring-forest/30 bg-forest/5" : "border-line bg-white hover:border-forest/40"
        }`}
        data-testid={`design-${styleKey}`}
      >
        <div
          className="h-20 flex items-center justify-center relative"
          style={{ background: p.bg, color: p.fg }}
        >
          <span className={`${fontClass} tracking-tight`} style={{ fontSize: 24, color: p.fg }}>
            {p.label}
          </span>
          <span
            className="absolute top-2 right-2 w-2 h-2 rounded-full"
            style={{ background: p.accent }}
          />
          {active && (
            <span className="absolute top-2 left-2 w-5 h-5 rounded-full bg-forest text-cream flex items-center justify-center">
              <Check size={12} weight="bold" />
            </span>
          )}
        </div>
        <div className="px-3 py-2.5">
          <div className="text-xs font-semibold text-ink truncate">{label}</div>
        </div>
      </button>
      {/* Tooltip on hover */}
      <div
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-30 w-64 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        data-testid={`design-tooltip-${styleKey}`}
      >
        <div className="bg-ink text-cream rounded-lg p-3 shadow-xl text-[11px] leading-[1.5]">
          {desc}
        </div>
        <div className="w-2 h-2 bg-ink absolute -top-1 left-1/2 -translate-x-1/2 rotate-45" />
      </div>
    </div>
  );
}

function Step6({ form, set, brands }) {
  const { t, lang } = useI18n();
  return (
    <div>
      <Heading kicker="06 · language & format" title={t("step6_title")} sub={t("step6_sub")} />
      <div className="mb-6">
        <div className="kicker-label mb-3">Language</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="language-group">
          <CardOption active={form.language === "el"} onClick={() => set({ language: "el" })} title={t("lang_el")} desc="Ελληνικά" testid="language-el" />
          <CardOption active={form.language === "en"} onClick={() => set({ language: "en" })} title={t("lang_en")} desc="English" testid="language-en" />
        </div>
      </div>
      <div className="mb-6">
        <div className="kicker-label mb-3">{t("format_label")}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="format-group">
          {Object.entries(FORMATS).map(([k, v]) => (
            <CardOption
              key={k}
              active={form.format === k}
              onClick={() => set({ format: k })}
              title={k.replace("x", ":")}
              desc={v.label}
              testid={`format-${k}`}
            />
          ))}
        </div>
      </div>
      <div>
        <div className="kicker-label mb-3 flex items-center gap-2">
          <Palette size={12} weight="duotone" /> {lang === "el" ? "Brand (προαιρετικό)" : "Brand (optional)"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="brand-picker">
          <BrandChoice active={!form.brand_id} onClick={() => set({ brand_id: "" })} testid="brand-choice-none">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg border border-dashed border-line bg-cream-deep flex items-center justify-center text-ink-soft text-[10px] uppercase tracking-wider">None</div>
              <div>
                <div className="font-heading font-medium text-ink text-sm">{lang === "el" ? "Χωρίς brand" : "No brand"}</div>
                <div className="text-xs text-ink-soft">{lang === "el" ? "Καθαρά slides, χωρίς logo" : "Clean slides, no logo"}</div>
              </div>
            </div>
          </BrandChoice>
          {brands.map((b) => (
            <BrandChoice key={b.id} active={form.brand_id === b.id} onClick={() => set({ brand_id: b.id })} testid={`brand-choice-${b.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cream-deep border border-line flex items-center justify-center overflow-hidden">
                  {b.logo_url ? <img src={fileUrl(b.logo_url)} alt="" className="w-full h-full object-contain" /> : <span className="w-5 h-5 rounded" style={{ background: b.brand_color || "#244A36" }} />}
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-medium text-ink text-sm truncate">{b.name || "Untitled"}</div>
                  <div className="text-xs text-ink-soft truncate">{b.handle || b.website || "—"}</div>
                </div>
              </div>
            </BrandChoice>
          ))}
        </div>
        {brands.length === 0 && (
          <div className="mt-3 text-xs text-ink-soft p-3 rounded-lg border border-dashed border-line flex items-center gap-2">
            <Plus size={12} />
            {lang === "el"
              ? "Κανένα brand ακόμη. Φτιάξε ένα από το εικονίδιο Brands στο menu για να εφαρμοστεί logo/handle/website στα slides."
              : "No brands yet. Create one from the Brands menu to apply logo/handle/website to slides."}
          </div>
        )}
      </div>
    </div>
  );
}

function BrandChoice({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`text-left p-4 rounded-2xl border transition ${
        active ? "border-forest bg-forest/5 ring-2 ring-forest/20" : "border-line bg-white hover:border-forest/40"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">{children}</div>
        {active && <Check size={16} weight="bold" className="text-forest flex-shrink-0 ml-2" />}
      </div>
    </button>
  );
}
